import React, { useState, useEffect } from "react";
import { Title } from "../router";
import axios from "axios";

const ITEMS_PER_PAGE = 10;

export const UserList = () => {
  const [alert, setAlert] = useState([]);
  const [autoApprove, setAutoApprove] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const headers = {
    headers: {
      authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  const formatTime12hFromTime = (timeStr) => {
    if (!timeStr) return "-";
    const [hour, minute, second] = timeStr.split(":");
    const date = new Date();
    date.setHours(hour, minute, second || 0);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split("T")[0];
  };

  const isRecent = (createdAt) =>
    Date.now() - new Date(createdAt).getTime() <= 2000;

  /* 🔥 SORT BY DATE + TIME (LATEST FIRST) */
  const sortAlertsByTime = (alerts = []) => {
    return [...alerts].sort((a, b) => {
      const aTime = new Date(`${a.created_at}T${a.created_time || "00:00:00"}`);
      const bTime = new Date(`${b.created_at}T${b.created_time || "00:00:00"}`);
      return bTime - aTime;
    });
  };

  const getAutoApprovalStatus = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_V2_URL}/admin/check-auto-approval`,
        headers
      );
      setAutoApprove(res.data.enabled);
    } catch {}
  };

  const getAllAlert = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}all-alerts`,
        {
          ...headers,
          params: { t: Date.now() },
        }
      );
      setAlert(res.data.all_alerts || []);
    } catch (err) {
      console.error("Failed to load alerts", err);
      setAlert([]);
    } finally {
      setLoading(false);
    }
  };

  const approveAlert = async (id) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_V2_URL}/admin/approve-alert/${id}`,
        {},
        headers
      );
      getAllAlert();
    } catch {
      alert("Approval window expired");
    }
  };

  useEffect(() => {
    getAutoApprovalStatus();
    getAllAlert();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [alert.length]);

  useEffect(() => {
    const interval = setInterval(getAllAlert, 10000);
    return () => clearInterval(interval);
  }, []);

  const sortedAlerts = sortAlertsByTime(alert);
  const totalPages = Math.ceil(sortedAlerts.length / ITEMS_PER_PAGE);

  const paginatedAlerts = sortedAlerts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <section className="p-8 rounded-lg shadow-s1">
      <div className="flex justify-between items-center">
        <Title level={5} className="font-normal">
          Content List
        </Title>

        {!autoApprove && (
          <span className="text-sm text-orange-600">
            Auto approval OFF
          </span>
        )}
      </div>

      <hr className="my-5" />

      {loading && (
        <div className="text-sm text-gray-400 mb-3">
          Loading latest content…
        </div>
      )}

      <div className="relative overflow-x-auto rounded-lg">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-100">
            <tr>
              <th className="px-6 py-5">#</th>
              <th className="px-6 py-5">Content</th>
              <th className="px-6 py-5">Time</th>
              <th className="px-6 py-5">Date</th>
              <th className="px-6 py-5">Action</th>
            </tr>
          </thead>

          <tbody>
            {paginatedAlerts.map((item, index) => (
              <tr key={item.id} className="bg-white border-b hover:bg-gray-50">
                <td className="px-6 py-4">
                  {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                </td>

                <td className="px-8 py-6 capitalize">
                  {item.content || "-"}
                </td>

                <td className="px-1 py-6 text-blue-500">
                  {formatTime12hFromTime(item.created_time)}
                </td>

                <td className="px-6 py-6 text-red-500 w-[200px]">
                  {formatDate(item.created_at)}
                </td>

                <td className="px-6 py-6">
                  {item.approved ? (
                    <span className="text-green-600 font-semibold">
                      Approved
                    </span>
                  ) : autoApprove ? (
                    <span className="text-blue-500">Auto</span>
                  ) : isRecent(item.created_at) ? (
                    <button
                      onClick={() => approveAlert(item.id)}
                      className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Approve
                    </button>
                  ) : (
                    <span className="text-gray-400">Expired</span>
                  )}
                </td>
              </tr>
            ))}

            {!loading && paginatedAlerts.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center py-6 text-gray-400">
                  No content found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-6">
        <span className="text-sm text-gray-500">
          Page {currentPage} of {totalPages || 1}
        </span>

        <div className="flex gap-2">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Prev
          </button>

          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
};
